/* eslint-disable @typescript-eslint/no-explicit-any */
import { Flow_0_0_2, getSelectedItemOne, ChangeManifestState, getCurrentUser, CustomerDetailConfig, DEFAULT_TENANT_DETAIL_CONFIG, getTenantConfig } from '@flexus/core';
import { UntypedFormControl, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import gql from 'graphql-tag';
import { flatten, values } from 'ramda';
import { empty, of, from, Observable, take, map, tap, withLatestFrom, switchMap, debounceTime, filter, skipWhile, catchError, startWith, merge } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { transformJobsToVouchersPayload, toggleUpfrontValidation, checkBusinessRules, buildDynamicMapper, buildAddressPayload, buildLoanInformationVoucherType, buildLoanInformationWhatIsBeingInstalled, buildAdditionalIdentitiesPayload } from './transform.functions';
import { CustomValidators, generateRange } from '@flexus/utilities';
import { CollapseActionPanel } from '../../../../app-shell-features';
import { RemoveError } from '@flexus/error-handler';

const SINGLE_ADDRESS_DETAILS: CustomerDetailConfig['addressDetails'] = {
	visible: true,
	commonFields: [
		{ controlName: 'complex', label: 'Estate / Office Park', storePath: ['loan_information.address.complex', 'loan_information.propertycomplex'] },
		{ controlName: 'street_address', label: 'street_address', storePath: 'loan_information.address.address' },
		{ controlName: 'suburb', label: 'suburb', storePath: 'loan_information.address.suburb' },
		{ controlName: 'city', label: 'city', inputProps: { noEndingSpace: true }, storePath: 'loan_information.address.city' },
		{ controlName: 'postal_code', label: 'postal_code', inputProps: { noEndingSpace: true }, storePath: 'loan_information.address.postal_code' },
		{
			controlName: 'province',
			label: 'province',
			type: 'dropdown',
			optionsSource: 'provinceList',
			placeholder: 'Select Province',
			storePath: 'loan_information.address.claimprovince'
		}
	],
	houseEstateFields: [],
	flatOfficeFields: []
};

export const normalizeTenantConfigAddressDetails = (tenantConfig: CustomerDetailConfig): CustomerDetailConfig => ({
	...tenantConfig,
	addressDetails: SINGLE_ADDRESS_DETAILS
});

export const getTenantAddressFields = (tenantConfig: CustomerDetailConfig) => [
	...(tenantConfig?.addressDetails?.commonFields ?? []),
	...(tenantConfig?.addressDetails?.houseEstateFields ?? []),
	...(tenantConfig?.addressDetails?.flatOfficeFields ?? [])
];

const APPOINTMENT_MIN_HOUR = 8;
const APPOINTMENT_MAX_HOUR = 17;

export const getAppointmentEarliestDateTime = (referenceDate: Date = new Date()): Date => {
	const earliest = new Date(referenceDate);
	earliest.setDate(earliest.getDate() + 2);

	const hour = earliest.getHours();
	const minute = earliest.getMinutes();
	if (hour < APPOINTMENT_MIN_HOUR) {
		earliest.setHours(APPOINTMENT_MIN_HOUR, 0, 0, 0);
	} else if (hour > APPOINTMENT_MAX_HOUR || (hour === APPOINTMENT_MAX_HOUR && minute > 0)) {
		earliest.setDate(earliest.getDate() + 1);
		earliest.setHours(APPOINTMENT_MIN_HOUR, 0, 0, 0);
	}

	while (earliest.getDay() === 0) {
		earliest.setDate(earliest.getDate() + 1);
		earliest.setHours(APPOINTMENT_MIN_HOUR, 0, 0, 0);
	}

	return earliest;
};

const buildAppointmentDateTime = (appointmentDate: any, appointmentTimePicker: any): Date | null => {
	if (!appointmentDate || !appointmentTimePicker) return null;
	const hour = Number(appointmentTimePicker?.hour);
	const minutes = Number(appointmentTimePicker?.minutes);
	if (!Number.isFinite(hour) || !Number.isFinite(minutes)) return null;
	const date = new Date(appointmentDate);
	if (Number.isNaN(date.getTime())) return null;
	date.setHours(hour, minutes, 0, 0);
	return date;
};

const isAppointmentDateTimeAllowed = (appointmentDateTime: Date, earliestDateTime: Date): boolean => {
	const hour = appointmentDateTime.getHours();
	const minutes = appointmentDateTime.getMinutes();
	const isWithinBusinessHours =
		hour >= APPOINTMENT_MIN_HOUR &&
		(hour < APPOINTMENT_MAX_HOUR || (hour === APPOINTMENT_MAX_HOUR && minutes === 0));
	const isSunday = appointmentDateTime.getDay() === 0;
	return isWithinBusinessHours && !isSunday && appointmentDateTime >= earliestDateTime;
};

const CLAIM_SUBMISSION_OPTIONAL_CONTROL_PATHS = [
	'insured_address.complex',
	'date_of_loss',
	'whatmatters',
	'failedwhatmattersreason',
	'whatmattersfailreason',
	'on_site_notes'
];

const CLAIM_SUBMISSION_OPTIONAL_JOB_FIELDS = ['collection_required', 'store_has_stock', 'delivery_cost'];

const stripControlValidators = (control: any) => {
	if (!control) return;
	control.clearValidators();
	control.updateValueAndValidity({ emitEvent: false });
};

const setOrClearCustomError = (control: any, errorKey: string, shouldSet: boolean) => {
	if (!control) return;
	const currentErrors = control.errors ?? {};

	if (shouldSet) {
		if (!currentErrors[errorKey]) {
			control.setErrors({ ...currentErrors, [errorKey]: true });
		}
		return;
	}

	if (!currentErrors[errorKey]) return;
	const rest = { ...currentErrors };
	delete rest[errorKey];
	control.setErrors(Object.keys(rest).length ? rest : null);
};

const syncClaimSubmissionValidation = (bf: any) => {
	CLAIM_SUBMISSION_OPTIONAL_CONTROL_PATHS.forEach(path => {
		stripControlValidators(bf.bigForm.get(path));
	});

	const jobs = bf.bigForm.get('jobs') as UntypedFormArray;
	if (jobs?.controls?.length) {
		jobs.controls.forEach((jobControl: any) => {
			CLAIM_SUBMISSION_OPTIONAL_JOB_FIELDS.forEach(field => {
				stripControlValidators(jobControl?.get(field));
			});
		});
	}

	const visitedControlNames = [
		'checked_customer_details',
		'checked_address_confirmation',
		'checked_job_details',
		'checked_appointments'
	];
	visitedControlNames.forEach(name => {
		const control = bf.bigForm.get(name);
		if (!control) return;
		control.setValidators([Validators.requiredTrue]);
		control.updateValueAndValidity({ emitEvent: false });
	});

	// Validate that appointments are actually set, not just that the tab was visited.
	// Setting the value to false makes the existing requiredTrue validator block submission.
	const appointmentsCheckControl = bf.bigForm.get('checked_appointments');
	if (appointmentsCheckControl?.value === true) {
		const jobValues = bf.bigForm.get('jobs')?.value ?? [];
		const upfrontrepudiation = !!bf.bigForm.get('upfrontrepudiation')?.value;
		const hasInvalidAppointments = hasInvalidAppointmentSelection(
			jobValues,
			upfrontrepudiation,
			getAppointmentEarliestDateTime()
		);
		if (hasInvalidAppointments) {
			appointmentsCheckControl.setValue(false, { emitEvent: false });
			appointmentsCheckControl.updateValueAndValidity({ emitEvent: false });
		}
	}

	let termsControl = bf.bigForm.get('accept_terms_and_conditions');
	if (!termsControl) {
		termsControl = new UntypedFormControl(false, [Validators.requiredTrue]);
		bf.bigForm.addControl('accept_terms_and_conditions', termsControl);
	} else {
		termsControl.setValidators([Validators.requiredTrue]);
		termsControl.updateValueAndValidity({ emitEvent: false });
	}

	const supportingDocsCheckControl = bf.bigForm.get('checked_supporting_document_upload');
	if (!supportingDocsCheckControl) return;

	supportingDocsCheckControl.setValidators([Validators.requiredTrue]);

	const requiredDocuments = (bf.bigForm.get('tenant_document_config')?.value?.required_documents ?? []).filter(
		(doc: any) => doc?.required !== false
	);
	const uploadedDocuments = bf.bigForm.get('supporting_docs')?.value ?? [];
	const uploadedDocumentTypes = new Set(
		(Array.isArray(uploadedDocuments) ? uploadedDocuments : [])
			.map((doc: any) => doc?.id ?? doc?.documentType ?? null)
			.filter(Boolean)
	);
	const missingRequiredDocuments = requiredDocuments.filter((doc: any) => !uploadedDocumentTypes.has(doc?.type));
	const hasAllRequiredDocumentsStoredLocally = missingRequiredDocuments.length === 0;

	supportingDocsCheckControl.setValue(hasAllRequiredDocumentsStoredLocally, { emitEvent: false });
	supportingDocsCheckControl.updateValueAndValidity({ emitEvent: false });
	setOrClearCustomError(
		supportingDocsCheckControl,
		'requiredLocalDocuments',
		!hasAllRequiredDocumentsStoredLocally
	);
};

const requiresAppointment = (job: any, upfrontrepudiation: boolean): boolean => {
	const providerType = Number(job?.providertype_id);
	const skillId = Number(job?.skill_id);
	return providerType !== 2 && skillId !== 44 && !upfrontrepudiation;
};

export const hasInvalidAppointmentSelection = (jobs: any[], upfrontrepudiation: boolean, earliestDateTime: Date): boolean => {
	return (jobs ?? []).some(job => {
		if (!requiresAppointment(job, upfrontrepudiation)) {
			return false;
		}
		const appointmentDateTime = buildAppointmentDateTime(job?.appointmentDatePicker, job?.appointmentTimePicker);
		if (!appointmentDateTime) {
			return true;
		}
		return !isAppointmentDateTimeAllowed(appointmentDateTime, earliestDateTime);
	});
};

// Tenant-specific overrides for customer detail configs
// These can be extended with tenant-specific field configurations
// Exported so pgg-edit-claim.ts can reuse the same overrides when reconstituting tenant config from a draft
export const TENANT_CONFIG_OVERRIDES: Record<string, CustomerDetailConfig> = {
	3: {
		customerDetails: {
			visible: true,
			fields: [
				{ controlName: 'first_name', label: 'first_name', storePath: 'applicant.first_name' },
				{ controlName: 'surname', label: 'surname', storePath: 'applicant.surname' },
				{ controlName: 'id_number', label: 'id_number', storePath: ['applicant.id_number', 'applicant.information.id_number'] }
			]
		},
		contactDetails: {
			visible: true,
			fields: [
				{ controlName: 'contact_number', label: 'contact_number', storePath: 'applicant.contact_number' },
				{
					controlName: 'cell_number',
					label: 'cell_number',
					storePath: ['applicant.cell_number', 'applicant.cellnumber', 'loan_information.cellnumber']
				},
				{ controlName: 'email', label: 'email', storePath: ['loan_information.email', 'applicant.email'] },
				{
					controlName: 'preferred_com_method',
					label: 'preferred_com_method',
					type: 'dropdown',
					optionsSource: 'commsOptions',
					placeholder: 'Select method',
					storePath: ['loan_information.preferedcommethod', 'loan_information.preferred_com_method']
				}
			]
		},
		addressDetails: SINGLE_ADDRESS_DETAILS
	},
	4: {
		customerDetails: {
			visible: true,
			fields: [
				{ controlName: 'first_name', label: 'first_name', storePath: 'applicant.first_name' },
				{ controlName: 'surname', label: 'surname', storePath: 'applicant.surname' },
				{ controlName: 'id_number', label: 'id_number', storePath: ['applicant.id_number', 'applicant.information.id_number'] }
			]
		},
		contactDetails: {
			visible: true,
			fields: [
				{ controlName: 'contact_number', label: 'contact_number', storePath: 'applicant.contact_number' }
			]
		},
		addressDetails: SINGLE_ADDRESS_DETAILS
	},
	9: {
		customerDetails: DEFAULT_TENANT_DETAIL_CONFIG.customerDetails,
		contactDetails: {
			visible: true,
			fields: [
				{ controlName: 'contact_number', label: 'contact_number', storePath: 'applicant.contact_number' },
				{ controlName: 'email', label: 'email', storePath: ['loan_information.email', 'applicant.email'] },
				{
					controlName: 'preferred_com_method',
					label: 'preferred_com_method',
					type: 'dropdown',
					optionsSource: 'commsOptions',
					placeholder: 'Select method',
					storePath: ['loan_information.preferedcommethod', 'loan_information.preferred_com_method']
				}
			]
		},
		addressDetails: SINGLE_ADDRESS_DETAILS
	},
	11: {
		customerDetails: {
			visible: true,
			fields: [
				{ controlName: 'first_name', label: 'first_name', storePath: 'applicant.first_name' },
				{ controlName: 'surname', label: 'surname', storePath: 'applicant.surname' },
				// { controlName: 'id_number', label: 'id_number', storePath: ['applicant.id_number', 'applicant.information.id_number'] }
			]
		},
		contactDetails: {
			visible: true,
			fields: [
				{ controlName: 'contact_number', label: 'contact_number', storePath: 'applicant.contact_number' },
				{
					controlName: 'cell_number',
					label: 'cell_number',
					storePath: ['applicant.cell_number', 'applicant.cellnumber', 'loan_information.cellnumber']
				},
				{ controlName: 'email', label: 'email', storePath: ['loan_information.email', 'applicant.email'] }
			]
		},
		addressDetails: SINGLE_ADDRESS_DETAILS
	}
};

/**
 * Builds the complete bigFormToStoreMapper by merging static mappings with dynamic tenant-specific mappings
 */
export function buildCompleteMapper(bf: any): Record<string, any> {
	// Get tenant config based on selected company
	const selectedCompany = bf?.bigForm?.get('selected_company')?.value ?? null;
	const metadata = bf?.bigForm?.get('tenant_document_config')?.value?.metadata;
	const tenantConfig = normalizeTenantConfigAddressDetails(getTenantConfig(selectedCompany, metadata, TENANT_CONFIG_OVERRIDES));
	
	// Build dynamic mapper from tenant config
	const dynamicMapper = buildDynamicMapper(tenantConfig);
	
	// Static mappings (existing hardcoded mappings)
	const staticMapper: Record<string, any> = {
		new_state: [
			(val) => (val != null ? val : 8),
			'new_state'
		],
		// sub_section: [
		// 	(val, storeObj, formValue) => {
		// 		if (checkPolicyType(formValue.underwriter) === 'StanSure') {
		// 			return 2;
		// 		} else {
		// 			return 1;
		// 		}
		// 	},
		// 	'sub_section_id'
		// ],
		// General
		handler: 'loan_information.handler',
		handlercontact: 'loan_information.handlercontact',
		handleremail: 'loan_information.handleremail',
		
		// Address confirmation (non-grouped fields)
		// latitude: ['location.lat', 'loan_information.claimlatitude'], // EXAMPLE
		latitude: ['location.lat'],
		longitude: ['location.long'],
		// province: ['claimprovince', 'loan_information.claimprovince'],
		addressconfirmation: [buildAddressPayload, 'loan_information.address'],
		area_code: 'loan_information.address.area_id',
		address_store_poi: 'loan_information.address.store_poi',
		address_searched: 'loan_information.address.address_searched',
		address_found: 'loan_information.address.address_found',
		address_from_lat: 'loan_information.address.from_lat',
		address_to_lat: 'loan_information.address.to_lat',
		address_from_lng: 'loan_information.address.from_lng',
		address_to_lng: 'loan_information.address.to_lng',
		address_suburb: 'loan_information.address.suburb',

		// Claim details
		claim_class: ['claim_type_id', 'claim_type'],
		// claim_class_description: 'loan_information.claimtype',
		jobs: [
			[transformJobsToVouchersPayload, 'loan_information.vouchers'],
			[buildLoanInformationVoucherType, 'loan_information.voucher_type'],
			[buildLoanInformationWhatIsBeingInstalled, 'loan_information.what_is_being_installed'],
			[buildAdditionalIdentitiesPayload, 'loan_information.additional_identities']
		],

		// Voucher monetary values → loan_information.values
		voucher_sp_value: 'loan_information.values.sp_value',
		voucher_client_value: 'loan_information.values.client_value',
		voucher_total_value: 'loan_information.values.total_value',

		// Claim Appointment
		on_site_contact: 'loan_information.onsitecontact',
		on_site_notes: 'loan_information.onsitenotes',
		on_site_person: 'loan_information.onsiteperson',
		accept_terms_and_conditions: 'loan_information.accept_terms_and_conditions',

		// Other
		// upfrontrepudiation: ['loan_information.upfrontrepudiation'],
		// upfrontrepudiationreason: ['loan_information.upfrontrepudiationreason'],
		// dontpingsp: 'loan_information.dontpingsp',

		// What Matters Action Panel
		// date_of_loss: [date => moment(date)?.format('YYYY-MM-DD'), 'loan_information.dateofloss'],
		whatmatters_from_voucher: 'loan_information.whatmatters',
		selected_company: 'tenant_id',
		'tenant_document_config.company_id': 'tenant_id',
		// claimdescription: [
		// 	(claimdescription, storeObj, formValues) => {
		// 		const handler = storeObj?.identity?.currentUser?.user?.full_name;
		// 		return 'Date: ' + moment()?.format('YYYY-MM-DD') + ' \nStaff: ' + (handler || '') + ' \n' + formValues.whatmatters + '.';
		// 	},
		// 	'loan_information.claimdescription'
		// ]
	};
	
	// Merge static and dynamic mappers (dynamic takes precedence for overlapping keys)
	return { ...staticMapper, ...dynamicMapper };
}

export const runSupportingDocumentExtraction = ({
	bf,
	http,
	modalService,
	onSuccess
}: {
	bf: any;
	http: any;
	modalService: any;
	onSuccess: () => void;
}) => {
	// Tenant-driven switch: ONLY run AI extraction when explicitly enabled by tenant config
	// (If missing/false, we must not call analyse_files at all.)
	const analyseFilesWithAi = bf?.bigForm?.get('tenant_document_config')?.value?.analyse_files_with_ai === true;
	if (!analyseFilesWithAi) {
		console.log('AI document extraction disabled for tenant (analyse_files_with_ai=false). Skipping extraction.');
		onSuccess();
		return;
	}

	// Skip extraction if data already exists in bigForm
	const existingData = bf.bigForm.get('extracted_document_data')?.value;
	if (existingData && Object.keys(existingData).length > 0) {
		console.log('Extraction data already exists, skipping extraction');
		onSuccess();
		return;
	}

	const supportingDocs = bf.bigForm.get('supporting_docs')?.value;

	// If no documents, navigate directly
	if (!supportingDocs || supportingDocs.length === 0) {
		onSuccess();
		return;
	}

	const tenant_id = bf.bigForm.get('tenant_document_config')?.value?.company_id;
	const modal = modalService;
	let modalInstance: any = null;

	// CSS spinner style
	const spinnerHtml = `<div style="display: flex; justify-content: center; margin: 20px 0;">
		<div style="width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite;"></div>
	</div>
	<style>@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }</style>`;

	// Open progress modal
	modal.openModalDirectly((inst: any) => {
		modalInstance = inst;
		inst.type = 'info';
		inst.heading = 'Extracting Documents';
		inst.subheading = `Processing documents ...`;
		inst.setMessage([spinnerHtml, '<p style="font-size: 1.1rem; line-height: 1.6; margin: 0.5rem 0;">Please wait while we extract information from your documents.</p>']);
		inst.showCloseButton = false;
		inst.navButtons = [];
	});

	// Process documents sequentially using fetch API
	const processDocuments = async () => {
		let result;
		const files = supportingDocs.map(d => ({
			name: d.name,
			document: d.base64,
			purpose: d.purpose
		}));

		http
			.post(`${environment.api_url}v1/file_action/analyse_files/`, {
				tenant_id,
				files
			})
			.pipe(take(1))
			.subscribe((res) => {
				result = res.payload;

				// Store merged data in bigForm
				const extractedDataControl = bf.bigForm.get('extracted_document_data');
				if (extractedDataControl) {
					extractedDataControl.setValue(result);
				} else {
					bf.bigForm.addControl('extracted_document_data', new UntypedFormControl(result));
				}
				localStorage.setItem('supporting_docs_metadata', JSON.stringify(result ?? {}));
				console.log('Extraction complete:', result);

				// Close modal and navigate
				if (modalInstance) {
					modalInstance.close();
				}
				onSuccess();
			});
	};

	processDocuments().catch((err) => {
		console.error('Extraction failed:', err);
		modal.openModalDirectly((inst: any) => {
			inst.type = 'warning';
			inst.heading = 'Extraction Failed';
			inst.message = 'Could not extract document data. You can continue and enter details manually.';
			inst.navButtons = [
				{
					text: 'Continue Anyway',
					color: 'primary',
					linkType: 'close',
					clickHandler: () => {
						onSuccess();
					}
				}
			];
		});
	});
};

export const PggCreateClaim: Flow_0_0_2 = {
	id: 'create_item_one',
	name: 'new claim',
	startNode: 'CompanySelection',
  serverCalls: {
		provinceList: {
			errorMessage: '',
			directCall: (http, store, sq) => {
				return sq
					.queryObject(
						gql`
							{
								allInfo {
									active_regions
								}
							}
						`,
						store
					)
					.pipe(
						take(1),
						map(res => {
							return (res as any)?.active_regions;
						})
					);
			}
		}
	},
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	deactivateStateGuard: (guardService: any, _comp, router, _currentRoute, currentState, nextState) => {
		console.log('🛡️ Guard triggered');

		// One-time bypass for submit-driven transitions from create flow.
		if (guardService?.pggService?.allowNextDeactivate) {
			guardService.pggService.allowNextDeactivate = false;
			guardService?.canGo$?.next(true);
			return of(true);
		}

		// Bypass warning modals when we explicitly redirect to workflow (e.g. success-tick Continue button)
		const bypassGuard =
			!!router?.getCurrentNavigation?.()?.extras?.state?.flxBypassDeactivateGuard && nextState?.url?.startsWith('/workflow');
		if (bypassGuard) {
			guardService?.canGo$?.next(true);
			return guardService?.pggService?.clearAllSupportingDocuments?.().pipe(
				map(() => true),
				catchError(() => of(true))
			) ?? of(true);
		}

		// Allow in-flow transitions (e.g. Submit → SubmissionSuccess) without showing discard modal.
		// With runGuardsAndResolvers: 'always', the guard can run when flow state updates; same URL = same route, just node change.
		const currentPath = currentState?.url?.split('?')[0] ?? '';
		const nextPath = nextState?.url?.split('?')[0] ?? '';
		if (currentPath === nextPath && currentPath.includes('/workflow')) {
			guardService?.canGo$?.next(true);
			return of(true);
		}

		const destinationUrl = nextState.url; // Capture the destination URL
		return from(guardService.indexedDbService.currentItem.get('currentItem')).pipe(
			withLatestFrom(guardService.canGo$),
			switchMap(([currentItem, canGo]: any) => {
				console.log('🛡️ Guard state:', { 
					canGo, 
					hasCurrentItem: !!currentItem, 
					formTouched: guardService.bf.bigForm.touched,
					currentItem 
				});
				return <Observable<any>>guardService.store.select(getSelectedItemOne).pipe(
					take(1),
					switchMap((itemOne: any) => {
						console.log('🛡️ Guard itemOne:', itemOne);
						// Check if we should show confirmation modal (for drafts or touched forms)
						const shouldBlock = !canGo && (currentItem || guardService.bf.bigForm.touched);
						console.log('🛡️ Should block navigation:', shouldBlock);
						if (shouldBlock) {
							if (itemOne && itemOne?.tempKey && guardService.bf.bigForm.touched) {
								// Local draft has been edited
								console.log('🛡️ Case: Local draft edited');
								guardService.modalService.openModalDirectly((inst) => {
									inst.type = 'warning';
									inst.closeButton = 'false';
									inst.color = 'alert';
									// inst.message = '';
									inst.setMessage(['You are moving away from this local draft.', ' Your changes will be discarded!']);
									// instance.open();
									inst.navButtons = [
										{
											text: 'Discard Changes',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												// Clear supporting documents when discarding
												guardService.pggService?.clearAllSupportingDocuments().subscribe(() => {
													guardService.indexedDbService.currentItem.delete('currentItem').then(() => {
														inst.close();
														guardService.canGo$.next(true);
														router.navigate([destinationUrl]).then(() => {
															setTimeout(() => {
																guardService.resetCanGoVariable();
															}, 2500);
														});
													});
												});
											}
										},
										{
											text: 'Save Changes Locally',
											color: 'primary',
											linkType: 'close',
											clickHandler: ()=> {
												const localDraft = {
													...currentItem,
													tempKey: itemOne?.tempKey,
													state: 169
												};
												guardService.indexedDbService.claimInDraft.put(localDraft).then(() => {
													guardService.indexedDbService.currentItem.delete('currentItem');
													// Clear supporting documents when saving as draft
													guardService.pggService?.clearAllSupportingDocuments().subscribe(() => {
														inst.close();
														guardService.canGo$.next(true);
														router.navigate([destinationUrl]).then(() => {
															setTimeout(() => {
																guardService.resetCanGoVariable();
															}, 2500);
														});
													});
												});
											}
										},
										{
											text: 'Cancel',
											color: 'alert',
											linkType: 'close',
											clickHandler: ()=> {
												inst.close();
											}
										}
									];
								});
								return guardService.canGo$.asObservable();
							} else if (itemOne && itemOne?.tempKey && !guardService.bf.bigForm.touched) {
								// Local draft opened but not edited yet
								guardService.modalService.openModalDirectly((inst) => {
									inst.type = 'warning';
									inst.closeButton = 'false';
									inst.color = 'alert';
									// inst.message = '';
									inst.setMessage(['You are moving away from this local draft.']);
									// instance.open();
									inst.navButtons = [
										{
											text: 'Leave',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												// Clear supporting documents when leaving
												guardService.pggService?.clearAllSupportingDocuments().subscribe(() => {
													guardService.indexedDbService.currentItem.delete('currentItem').then(() => {
														inst.close();
														guardService.canGo$.next(true);
														router.navigate([destinationUrl]).then(() => {
															setTimeout(() => {
																guardService.resetCanGoVariable();
															}, 2500);
														});
													});
												});
											}
										},
										{
											text: 'Stay',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												inst.close();
											}
										}
									];
								});
								return guardService.canGo$.asObservable();
							} else if (itemOne && !itemOne.tempKey && !guardService.bf.bigForm.touched) {
								// Any Server Draft not edited
								guardService.modalService.openModalDirectly((inst) => {
									inst.type = 'warning';
									inst.closeButton = 'false';
									inst.color = 'alert';
									// inst.message = '';
									inst.setMessage(['You are moving away from your current view.']);
									// instance.open();
									inst.navButtons = [
										{
											text: 'Leave',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												// Clear supporting documents when leaving
												guardService.pggService?.clearAllSupportingDocuments().subscribe(() => {
													guardService.indexedDbService.currentItem.delete('currentItem').then(() => {
														inst.close();
														guardService.canGo$.next(true);
														router.navigate([destinationUrl]).then(() => {
															setTimeout(() => {
																guardService.resetCanGoVariable();
															}, 2500);
														});
													});
												});
											}
										},
										{
											text: 'Stay',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												inst.close();
											}
										}
									];
								});
								return guardService.canGo$.asObservable();
							} else if (itemOne && !itemOne.tempKey && guardService.bf.bigForm.touched) {
								// Any server draft that is edited
								guardService.modalService.openModalDirectly((inst) => {
									inst.type = 'warning';
									inst.closeButton = 'false';
									inst.color = 'alert';
									// inst.message = '';
									inst.setMessage(['You are moving away from this draft.', ' Your changes will be discarded!']);
									// instance.open();
									inst.navButtons = [
										{
											text: 'Discard Changes',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												// Clear supporting documents when discarding
												guardService.pggService?.clearAllSupportingDocuments().subscribe(() => {
													guardService.indexedDbService.currentItem.delete('currentItem').then(() => {
														inst.close();
														guardService.canGo$.next(true);
														router.navigate([destinationUrl]).then(() => {
															setTimeout(() => {
																guardService.resetCanGoVariable();
															}, 2500);
														});
													});
												});
											}
										},
										{
											text: 'Cancel',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												inst.close();
											}
										}
									];
								});
								return guardService.canGo$.asObservable();
							} else if (!itemOne && !guardService.bf.bigForm.touched) {
								// Creating New Claim but forms has not been touched yet
								guardService.modalService.openModalDirectly((inst) => {
									inst.type = 'warning';
									inst.closeButton = 'false';
									inst.color = 'alert';
									// inst.message = '';
									inst.setMessage(['You are moving from your current view.']);
									// instance.open();
									inst.navButtons = [
										{
											text: 'Leave',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												// Clear supporting documents when leaving
												guardService.pggService?.clearAllSupportingDocuments().subscribe(() => {
													guardService.indexedDbService.currentItem.delete('currentItem').then(() => {
														inst.close();
														guardService.canGo$.next(true);
														router.navigate([destinationUrl]).then(() => {
															setTimeout(() => {
																guardService.resetCanGoVariable();
															}, 2500);
														});
													});
												});
											}
										},
										{
											text: 'Stay',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												inst.close();
											}
										}
									];
								});
								return guardService.canGo$.asObservable();
							} else if (!itemOne && guardService.bf.bigForm.touched) {
								// In Claim creation and form has been touched
								console.log('🛡️ Case: New claim with touched form');
								guardService.modalService.openModalDirectly((inst) => {
									inst.type = 'warning';
									inst.closeButton = 'false';
									// inst.message = '';
									inst.setMessage(['You are moving from your current view.', ' Your changes will be discarded!']);
									// instance.open();
									inst.navButtons = [
										{
											text: 'Discard Changes',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												console.log('🔴 Discard Changes clicked');
												console.log('🔴 destinationUrl:', destinationUrl);
												console.log('🔴 router:', router);
												// Clear supporting documents when discarding
												guardService.pggService?.clearAllSupportingDocuments().subscribe({
													next: () => {
														console.log('🔴 Documents cleared');
														guardService.indexedDbService.currentItem.delete('currentItem').then(() => {
															console.log('🔴 Current item deleted');
															inst.close();
															console.log('🔴 Modal closed');
															guardService.canGo$.next(true);
															console.log('🔴 canGo$ set to true');
															console.log('🔴 Attempting navigation to:', destinationUrl);
															router.navigate([destinationUrl]).then(
																(success) => {
																	console.log('🔴 Navigation result:', success);
																	setTimeout(() => {
																		guardService.resetCanGoVariable();
																	}, 2500);
																},
																(error) => {
																	console.error('🔴 Navigation error:', error);
																}
															);
														});
													},
													error: (err) => {
														console.error('🔴 Error clearing documents:', err);
													}
												});
											}
										},
										{
											text: 'Cancel',
											color: 'alert',
											linkType: 'close',
											clickHandler: () => {
												inst.close();
											}
										}
									];
								});
								console.log('🛡️ Returning canGo$ observable');
								return guardService.canGo$.asObservable();
							} else {
								// Fallback: if we reach here, prevent navigation by default
								console.warn('🛡️ Unhandled navigation guard case', { itemOne, currentItem, touched: guardService.bf.bigForm.touched });
								return of(false);
							}
						} else {
							console.log('🛡️ Allowing navigation (else branch)');
							// When we don't need to block navigation, emit true as an observable
							return of(true);
						}
					})
				);
			})
		);
	},
	activateStateGuard: () => {
		return of(true);
	},
	// What Matters action panel disabled – uncomment to re-enable the side panel button
	// onStateInit: instance => {
	// 	const paramFunc = (inst, mapper) => {
	// 		return {
	// 			id: '1',
	// 			icon: 'what-matters',
	// 			command: inst.loadComponent.bind(inst, mapper['FLXWhatMattersComponent'])
	// 		};
	// 	};
	// 	instance.navService.portalActions.next({ call: 'onClick', paramFunc });
	// },
	onStateDestroy: instance => {
		// Clear supporting documents when flow is destroyed
		instance.pggService?.clearAllSupportingDocuments().subscribe();
	},
	itemType: 'flow',
	// actionPanel: instance => setActionPanelItems(instance, ['what-matters']),
	instructions: {
		editRoles: {
			0: 'Placeholder'
		},
		viewRoles: {
			0: 'Placeholder'
		}
	},
	header: {
		title: 'New Voucher',
		controls: () => () => []
	},
	footer: {
		type: 'node_nav'
	},
	events: {
		// checkCatastrophe: {
		// 	// sys. denotes system events
		// 	triggerOn: ['sys.online', 'date_of_loss', 'claim_class', 'latitude', 'longitude', 'suburb_code', 'province'],
		// 	triggerWhen: (online, date_of_loss, claim_class, latitude, longitude, suburb_code, province) =>
		// 		online && !!date_of_loss && date_of_loss instanceof Date && !isNaN(date_of_loss?.valueOf()) && !!claim_class && !!latitude && !!longitude && !!suburb_code && !!province,
		// 	dataMutations: () => {},
		// 	serverCalls: {
		// 		checkCatastrophe: {
		// 			errorMessage: 'Error could not check Catastrophe',
		// 			ignoreFalseError: true,
		// 			isBackgroundTask: true,
		// 			offlineFallback: {
		// 				errorMessage: 'Could not run CAT check, you are offline.',
		// 				directCall: () => {
		// 					return of('Is Offline');
		// 				}
		// 			},
		// 			directCall: (http, store, sq, bf) => {
		// 				const { date_of_loss, claim_class, suburb_code } = bf.bigForm.value;
		// 				const body = {
		// 					loss_date: moment(date_of_loss)?.format('YYYY-MM-DD'),
		// 					claim_type: claim_class,
		// 					postal_code: suburb_code
		// 				};
		// 				return http.post(`${environment.api_url}v1/cat_action/check_catastrophe/`, body).pipe(
		// 					filter(Boolean),
		// 					map((res: any) => res?.payload),
		// 					tap((res: any) => {
		// 						bf.bigForm.addControl('cat_checked', new UntypedFormControl(1));
		// 						if (res && res?.is_cat) {
		// 							bf.bigForm.addControl('is_cat', new UntypedFormControl(res?.is_cat || null));
		// 							bf.bigForm.addControl('cat_code', new UntypedFormControl(res?.cat_code || null));
		// 						}
		// 					})
		// 				);
		// 				// return of({ msg: 'testing cat check calls...' }).pipe(tap(console.log));
		// 			}
		// 		}
		// 	}
		// },
		// autoDontPingSPs: {
		// 	triggerOn: ['upfrontrepudiation'],
		// 	triggerWhen: upfrontrep => !!upfrontrep,
		// 	dataMutations: bf => {
		// 		if (bf.bigForm.get('dontpingsp') && bf.bigForm.get('previous_upfrontrepudiation')) {
		// 			bf.bigForm.get('dontpingsp').patchValue(1);
		// 			bf.bigForm.get('previous_upfrontrepudiation').patchValue(1);
		// 			toggleUpfrontValidation(bf.bigForm.get('upfrontrepudiation')?.value, bf.bigForm);
		// 		}
		// 	}
		// },
		// resetUpfront: {
		// 	triggerOn: ['upfrontrepudiation', 'previous_upfrontrepudiation'],
		// 	triggerWhen: (upfrontrep, prev) => !!upfrontrep === false && !!prev !== !!upfrontrep,
		// 	dataMutations: bf => {
		// 		if (bf.bigForm.get('dontpingsp') && bf.bigForm.get('previous_upfrontrepudiation')) {
		// 			bf.bigForm.get('dontpingsp').patchValue(0);
		// 			bf.bigForm.get('previous_upfrontrepudiation').patchValue(0);
		// 			if (bf.bigForm.get('upfrontrepudiation') && !!bf.bigForm.get('upfrontrepudiation')?.value) {
		// 				toggleUpfrontValidation(!!bf.bigForm.get('upfrontrepudiation')?.value, bf.bigForm);
		// 			} else {
		// 				checkBusinessRules(bf.bigForm);
		// 			}
		// 		}
		// 	}
		// }
	},
	nodes: {
		CompanySelection: {
			serverCalls: {
				companyList: {
					errorMessage: '',
					directCall: (http, store, sq) => {
						return sq.queryObject(gql`{ 
														identity { 
															currentUser {
																user {
																	branches
																}
															}
														}
														allInfo { 
															config_options {
																general {
																	tenant_config {
																		id
																		name
																		logo_url
																		logo_alt
																		demand_creation
																		analyse_files_with_ai
																		required_documents {
																			type
																			label
																			required
																		}
																		additional_identities {
																			identity_type
																			description
																		}
																	}
																}
															}
														} }`, store).pipe(
															take(1),
															map((res: any) => {
																console.log('companyList + ====== +', res);
																console.log('companyList: res type:', typeof res, 'isArray:', Array.isArray(res));
																console.log('companyList: res keys:', Object.keys(res || {}));
																
																// Check if res itself is the tenant_config array
																let tenantConfigs: any[] = [];
																
																if (Array.isArray(res)) {
																	// Response is directly an array
																	tenantConfigs = res;
																} else if (res?.allInfo?.config_options?.general?.tenant_config) {
																	// Standard nested path
																	tenantConfigs = res.allInfo.config_options.general.tenant_config;
																} else if (res?.config_options?.general?.tenant_config) {
																	// Without allInfo wrapper
																	tenantConfigs = res.config_options.general.tenant_config;
																} else if (res?.general?.tenant_config) {
																	// Even more direct
																	tenantConfigs = res.general.tenant_config;
																} else if (res?.tenant_config) {
																	// Direct tenant_config
																	tenantConfigs = res.tenant_config;
																} else if (res?.allInfo) {
																	// Check if allInfo has tenant_config directly
																	tenantConfigs = res.allInfo.tenant_config || [];
																}
																
																// Ensure it's an array
																if (!Array.isArray(tenantConfigs)) {
																	tenantConfigs = [];
																}
																
																console.log('companyList: tenantConfigs count:', tenantConfigs.length);
																const transformed = tenantConfigs
																	.filter((config: any) => {
																		// Only include companies where demand_creation is true
																		return config.demand_creation === true;
																	})
																	.map((config: any) => {
																		const required_documents = (config.required_documents || []).map((doc: any) => ({
																			type: String(doc.type || ''),
																			label: String(doc.label || ''),
																			required: Boolean(doc.required !== false),
																			allowed_file_types: ['pdf', 'jpg', 'jpeg', 'png'],
																			max_file_size_mb: 10,
																			validation_rules: {}
																		}));
																		
																		const demand_creation = Boolean(config.demand_creation === true);
																		const analyse_files_with_ai = config.analyse_files_with_ai === true;
																		
																		
																		const additional_identities = (config.additional_identities || []).map((ai: any) => ({
																			identity_type: String(ai.identity_type || ''),
																			description: String(ai.description || '')
																		}));

																		return {
																			id: Number(config.id),
																			name: String(config.name || ''),
																			logo_url: String(config.logo_url || ''),
																			logo_alt: String(config.logo_alt || config.name || ''),
																			required_documents,
																			additional_identities,
																			demand_creation: demand_creation,
																			analyse_files_with_ai,
																			next_node: demand_creation
																				? analyse_files_with_ai
																					? 'SupportingDocumentUpload'
																					: 'CustomerDetails'
																				: undefined,
																			metadata: { additional_identities },
																			source_urlstring: config.logo_url || null
																		};
																	});
																	const userBranches = res.branches;
																	console.log('companyList: userBranches:', userBranches);
																console.log('companyList: transformed count:', transformed.length);
																console.log('companyList: transformed data:', transformed);
																return transformed.filter((item: any) => userBranches.includes(item.id));
															})
														);
					}
				},
				userBranches: {
					errorMessage: '',
					directCall: (http, store, sq) => {
						return sq.queryObject(gql` 
							{ 
								identity { 
									currentUser {
										user {
											branches
										}
									}
								} 
							}`, store).pipe(
								take(1),
								map((res: any) => res?.identity?.currentUser?.user?.branches)
							);
					}
				},
			},
			name: 'New Voucher',
			// showTabs: true,
			hideTabItem: true,
			initFormFields: (bf, item, inst, sq, store) => {
				// bf.bigForm.addControl('validitytest', new UntypedFormControl(false, [Validators.requiredTrue]), );
				bf.bigForm.addControl('checked_company_selection', new UntypedFormControl(null));
				bf.bigForm.patchValue({ checked_company_selection: true });
				store.select(getCurrentUser).pipe(take(1)).subscribe(user => {
					const { full_name, contact_number } = user?.user ?? {};
					const email_address = user?.email ?? null;
					bf.bigForm.addControl('handler', new UntypedFormControl(full_name));
					bf.bigForm.addControl('handlercontact', new UntypedFormControl(contact_number));
					bf.bigForm.addControl('handleremail', new UntypedFormControl(email_address));
				});

				// To check that the screens have been visited
				
				bf.bigForm.addControl('checked_supporting_document_upload', new UntypedFormControl(null));
				bf.bigForm.addControl('checked_customer_details', new UntypedFormControl(null));
				bf.bigForm.addControl('checked_address_confirmation', new UntypedFormControl(null));
				bf.bigForm.addControl('checked_job_details', new UntypedFormControl(null));
				bf.bigForm.addControl('checked_appointments', new UntypedFormControl(null));
				
				// Initialize new_state and sub_section for mapper
				if (!bf.bigForm.get('new_state')) {
					bf.bigForm.addControl('new_state', new UntypedFormControl(8));
				}
				// if (!bf.bigForm.get('sub_section')) {
				// 	bf.bigForm.addControl('sub_section', new UntypedFormControl(1));
				// }
						// For maps initialisation. Otherwise validators do not pick it up
				let latitude, longitude, province, area_code;

				if (bf.bigForm.get('latitude')) {
					latitude = bf.bigForm.get('latitude')?.value;
					bf.bigForm.get('latitude')?.setValidators([Validators.required]);
				} else {
					bf.addControl('latitude', new UntypedFormControl(null, [Validators.required]));
				}	

				if (bf.bigForm.get('longitude')) {
					longitude = bf.bigForm.get('longitude')?.value;
					bf.bigForm.get('longitude')?.setValidators([Validators.required]);
				} else {
					bf.addControl('longitude', new UntypedFormControl(null, [Validators.required]));
				}

				

				if (bf.bigForm.get('province')) {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					province = bf.bigForm.get('province')?.value;
					bf.bigForm.get('province')?.setValidators([Validators.required]);
				} else {
					bf.addControl('province', new UntypedFormControl(null, [Validators.required]));
				}

				if (bf.bigForm.get('area_code')) {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					area_code = bf.bigForm.get('area_code')?.value;
					bf.bigForm.get('area_code')?.setValidators([Validators.required]);
				} else {
					bf.addControl('area_code', new UntypedFormControl(null, [Validators.required]));
				}

				if (!latitude || !longitude) {
					latitude = null;
					longitude = null;
				}
				// Clear any leftover supporting documents from previous sessions
				// This handles browser refresh scenarios
				
				inst.pggService?.clearAllSupportingDocuments().subscribe();
			},
			component: 'CompanySelectionComponent',
			inputs: {
				title: 'Select the company',
				instructions: ['Which company is the job for?'],
				nextNode: 'SupportingDocumentUpload',
				// clients: MOCK_COMPANY_CONFIGS
				clients$: 'companyList'
			},
			navs: [
				// What Matters nav button disabled – uncomment to re-enable
				// {
				// 	text: 'What Matters',
				// 	location: 'center',
				// 	linkType: 'portal',
				// 	portalData: {
				// 		type: 'actionPanel',
				// 		paramFunc: (instance, mapper) => {
				// 			return {
				// 				id: '1',
				// 				icon: 'what-matters',
				// 				command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
				// 			};
				// 		}
				// 	}
				// }
			]
		},
		SupportingDocumentUpload: {
			name: 'Documents',
			// checkValidityForFields: ['validitytest'],
			showTabs: true,
			initFormFields: (bf, _editableItem, compInstance, _storeQuery, store) => {
				bf.bigForm.patchValue({ checked_supporting_document_upload: true });

				// Wire up the Analyze with AI button (only for claim handlers - role_id 1)
				const tenantConfig = bf.bigForm.get('tenant_document_config')?.value;
				const analyseEnabled = tenantConfig?.analyse_files_with_ai === true;

				store.select(getCurrentUser).pipe(take(1)).subscribe(user => {
					const isClaimHandler = user?.user?.roles?.includes(1) === true;
					compInstance.analyseFilesWithAi = analyseEnabled && isClaimHandler;

					// Restore extraction state if user navigated away and came back
					const alreadyExtracted = bf.bigForm.get('extraction_complete')?.value === true;
					if (alreadyExtracted) {
						compInstance.extractionComplete.set(true);
					}

					if (analyseEnabled && isClaimHandler) {
						compInstance.onAnalyzeImages = () => {
							const modalService = compInstance._modalService;
							if (!modalService) return;

							modalService.openModalDirectly((inst: any) => {
								inst.type = 'info';
								inst.heading = 'Analyze Documents';
								inst.setMessage(['<p style="font-size: 1.1rem; line-height: 1.6; margin: 0.5rem 0;">AI extraction can take up to a minute to complete.</p>', '<p style="font-size: 1.1rem; line-height: 1.6; margin: 0.5rem 0;">Do you want to continue?</p>']);
								inst.showCloseButton = true;
								inst.navButtons = [
									{
										text: 'Cancel',
										color: 'default',
										linkType: 'close'
									},
									{
										text: 'Continue',
										color: 'primary',
										clickHandler: () => {
											runSupportingDocumentExtraction({
												bf,
												http: compInstance._http,
												modalService,
												onSuccess: () => {
													compInstance.extractionComplete.set(true);
													compInstance.cdr.markForCheck();
													// Persist state in bigForm so it survives navigation
													const ctrl = bf.bigForm.get('extraction_complete');
													if (ctrl) {
														ctrl.setValue(true);
													} else {
														bf.bigForm.addControl('extraction_complete', new UntypedFormControl(true));
													}
												}
											});
										}
									}
								];
							});
						};
					}
				});
			},
			component: 'SupportingDocumentUploadComponent',
			navs: [
				// What Matters nav button disabled – uncomment to re-enable
				// {
				// 	text: 'What Matters',
				// 	location: 'center',
				// 	linkType: 'portal',
				// 	portalData: {
				// 		type: 'actionPanel',
				// 		paramFunc: (instance, mapper) => {
				// 			return {
				// 				id: '1',
				// 				icon: 'what-matters',
				// 				command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
				// 			};
				// 		}
				// 	}
				// },
				{ 
					text: 'Continue',
					color: 'primary', 
					optIntoValidation: true,
					nextNode: 'CustomerDetails',
					beforeNextNode: (navComponent: any, onSuccess: () => void, _onFail: () => void) => {
						void _onFail;
						runSupportingDocumentExtraction({
							bf: navComponent.bf,
							http: navComponent.http,
							modalService: navComponent.newModalService,
							onSuccess: () => {
								const analyseFilesWithAi =
									navComponent?.bf?.bigForm?.get('tenant_document_config')?.value?.analyse_files_with_ai === true;
								const hasVisitedCustomerDetails =
									navComponent?.bf?.bigForm?.get('checked_customer_details')?.value === true;
								if (analyseFilesWithAi && !hasVisitedCustomerDetails) {
									onSuccess();
								} else {
									navComponent.nextComponentKey('TermsAndConditions');
								}
							}
						});
					}
				}
			]
		},

		
		CustomerDetails: {
			name: 'Customer Details',
			showTabs: true,
			initFormFields: (bf) => {
				bf.bigForm.patchValue({ checked_customer_details: true });

				// Get tenant config using the same logic as the mapper to ensure alignment
				const selectedCompany = bf.bigForm.get('selected_company')?.value ?? null;
				const metadata = bf.bigForm.get('tenant_document_config')?.value?.metadata;
				const tenantConfig = normalizeTenantConfigAddressDetails(getTenantConfig(selectedCompany, metadata, TENANT_CONFIG_OVERRIDES));

				// Validator mappings for common fields
				const getValidators = (controlName: string): any[] => {
					const validatorMap: Record<string, any[]> = {
						first_name: [
							Validators.required,
							Validators.minLength(2),
							CustomValidators.hardMaxLength(140),
							CustomValidators.onlyAllowedASCII([32, 39, 44, 46, ...generateRange(65, 90, 1), ...generateRange(96, 122, 1)])
						],
						surname: [
							Validators.required,
							Validators.minLength(2),
							CustomValidators.hardMaxLength(140),
							CustomValidators.onlyAllowedASCII([32, 39, 44, 46, ...generateRange(65, 90, 1), ...generateRange(96, 122, 1)])
						],
						id_number: [
							Validators.required,
							CustomValidators.onlyAllowedASCII(generateRange(48, 57, 1)),
							CustomValidators.alphaNumericWithForwardSlash,
							Validators.minLength(6),
							CustomValidators.hardMaxLength(13)
						],
						contact_number: [
							Validators.required,
							CustomValidators.onlyAllowedASCII(generateRange(48, 57, 1)),
							CustomValidators.contact_number,
							Validators.minLength(10),
							CustomValidators.hardMaxLength(12)
						],
						cell_number: [
							Validators.required,
							CustomValidators.onlyAllowedASCII(generateRange(48, 57, 1)),
							CustomValidators.numeric,
							Validators.minLength(10),
							CustomValidators.hardMaxLength(12)
						],
						email: [
							Validators.required,
							Validators.email,
							CustomValidators.onlyAllowedASCII(generateRange(32, 126, 1))
						],
						preferred_com_method: [Validators.required],
						complex: [CustomValidators.hardMaxLength(64)],
						complex_unit_number: [CustomValidators.hardMaxLength(64)],
						street_address: [Validators.required, CustomValidators.hardMaxLength(64)],
						suburb: [Validators.required, CustomValidators.hardMaxLength(64)],
						city: [Validators.required, CustomValidators.hardMaxLength(64)],
						province: [Validators.required, CustomValidators.hardMaxLength(64)],
						area_code: [Validators.required, CustomValidators.hardMaxLength(64)],
						postal_code: [Validators.required, CustomValidators.hardMaxLength(64)]
					};
					return validatorMap[controlName] ?? [];
				};

				// Build client_details form group dynamically based on tenant config
				const clientDetailsControls: Record<string, UntypedFormControl> = {};
				(tenantConfig?.customerDetails?.fields ?? []).forEach(field => {
					if (field.controlName) {
						const validators = field.validators ?? getValidators(field.controlName);
						clientDetailsControls[field.controlName] = new UntypedFormControl('', validators);
					}
				});
				bf.addControl('client_details', new UntypedFormGroup(clientDetailsControls));

				// Build contact_details form group dynamically based on tenant config
				const contactDetailsControls: Record<string, UntypedFormControl> = {};
				(tenantConfig?.contactDetails?.fields ?? []).forEach(field => {
					if (field.controlName) {
						const validators = field.validators ?? getValidators(field.controlName);
						contactDetailsControls[field.controlName] = new UntypedFormControl('', validators);
					}
				});
				bf.addControl('contact_details', new UntypedFormGroup(contactDetailsControls));

				// Build insured_address form group dynamically based on tenant config
				const addressFields = getTenantAddressFields(tenantConfig);
				const insuredAddressControls: Record<string, UntypedFormControl> = {};
				addressFields.forEach(field => {
					if (field.controlName) {
						const validators = field.validators ?? getValidators(field.controlName);
						insuredAddressControls[field.controlName] = new UntypedFormControl('', validators);
					}
				});
				bf.addControl('insured_address', new UntypedFormGroup(insuredAddressControls));
			},
			inputs: {
				commsOptions: [
					{ value: 'Phone', display: 'Phone' },
					{ value: 'Sms', display: 'SMS' },
					{ value: 'Email', display: 'Email' },
					{ value: 'Other', display: 'Other' }
				],
				// sourceToFormMapper: {
				// 	ADDITIONALEXCESS: 'additionalexcess',
				// 	FIRSTNAME: 'client_details.first_name',
				// 	SURNAME: 'client_details.surname',
				// 	IDNUMBER: 'client_details.id_number',
				// 	POLICYNUMBER: 'client_details.policy_number',
				// 	ACCOUNTNUMBER: 'client_details.bond_number',
				// 	HOMETELNUMBER: 'contact_details.contact_number',
				// 	CELLNUMBER: 'contact_details.cell_number',
				// 	EMAIL: 'contact_details.email',
				// 	LOCALFILE: 'local_file',
				// 	ISJOINTACCOUNT: 'is_joint_account',
				// 	CLIENTCODE: 'clientcode',
				// 	PREFEREDCONTACT: 'contact_details.preferred_com_method',
				// 	ADDRESS1: 'insured_address.street_address',
				// 	ADDRESS2: 'insured_address.suburb',
				// 	SUBURB: 'insured_address.city',
				// 	SUBURBCODE: 'suburb_code',
				// 	SUMINSURED: 'sum_insured',
				// 	// UNDERWRITERCODE: 'underwriter',
				// 	REINSURANCEEXCESS: 'reinsuranceexcess',
				// 	SPECIALCLAUSE: 'specialclause',
				// 	VOLEXCESS: 'voluntaryexcess',
				// 	MARKETSEGMENTATIONREF_KEY: 'market_segment_key'
				// }
			},
			component: {
				children: [
					{
						component: 'PinggoCustomerDetailComponent',
						inputs: {
							commsOptions:  [
								{ value: 'Phone', display: 'Phone' },
								{ value: 'Sms', display: 'SMS' },
								{ value: 'Email', display: 'Email' },
								{ value: 'Other', display: 'Other' }
							],
							sourceToFormMapper: 'sourceToFormMapper'
						}
					},
					{
						component: 'FLXSupportingDocumentFilePreviewComponent',
						inputs: {}
					}
				]
			},
			navs: [
				// What Matters nav button disabled – uncomment to re-enable
				// {
				// 	text: 'What Matters',
				// 	location: 'center',
				// 	linkType: 'portal',
				// 	portalData: {
				// 		type: 'actionPanel',
				// 		paramFunc: (instance, mapper) => {
				// 			return {
				// 				id: '1',
				// 				icon: 'what-matters',
				// 				command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
				// 			};
				// 		}
				// 	}
				// },
				{
					text: 'Continue',
					location: 'right',
					nextNode: 'AddressConfirmation',
					color: 'primary'
				}
			]
		},
		AddressConfirmation: {
			name: 'Maps',
			showTabs: true,
			initFormFields: (bf) => {
				bf.bigForm.patchValue({ checked_address_confirmation: true });
			},
			serverCalls: {
				provinceList: {
					errorMessage: '',
					directCall: (http, store, sq) => {
						return sq
							.queryObject(
								gql`
									{
										allInfo {
											active_regions
										}
									}
								`,
								store
							)
							.pipe(
								take(1),
								map(res => {
									return (res as any)?.active_regions;
								})
							);
					}
				},
				policyAddressObject: {
					errorMessage: '',
					directCall: (http, store, sq) => {
						// Clearing Claim History Error Message, Had to add it to multiple nodes as there is no deactivate per node. // check this comment!
						// propertycomplex
						// propertycomplexunitnumber
						// propertystreetaddress
						// propertysuburb
						// propertycity
						// suburbcode
						store.dispatch(new RemoveError({ dataKey: 'claimHistory' }));
						return sq
							.queryObject(
								gql`
									{
										selectedContext {
											submissionData {
												loan_information {
													address {
														claimprovince
														address
														suburb
														city
														postal_code
													}
												}
											}
										}
									}
								`,
								store
							)
							.pipe(
								skipWhile(f => !f),
								take(1)
							);
					}
				},
				policyAddress: {
					errorMessage: '',
					directCall: (http, store, sq) => {
						// Clearing Claim History Error Message, Had to add it to multiple nodes as there is no deactivate per node.
						// propertycomplex
						// propertycomplexunitnumber
						// propertycomplexblock
						// propertystreetnumber
						// propertystreetname
						// propertysuburb
						// propertycity
						// suburbcode
						store.dispatch(new RemoveError({ dataKey: 'claimHistory' }));
						return sq
							.queryObject(
								gql`
									{
										selectedContext {
											submissionData {
												loan_information {
													address {
														claimprovince
														address
														suburb
														city
														postal_code
													}
													
												}
											}
										}
									}
								`,
								store
							)
							.pipe(
								skipWhile(f => !f),
								take(1),
								map(result => {
									const res = result as any;
									const addressArray = [];

									// if (res?.propertycomplex) addressArray.push(res?.propertycomplex);
									// if (res?.propertycomplexunitnumber) addressArray.push(res?.propertycomplexunitnumber);
									// if (res?.propertycomplexblock) addressArray.push(res?.propertycomplexblock);
									// if (res?.propertystreetnumber) addressArray.push(res?.propertystreetnumber);
									// if (res?.propertystreetname) addressArray.push(res?.propertystreetname);
									// if (res?.propertysuburb) addressArray.push(res?.propertysuburb);
									// if (res?.propertycity) addressArray.push(res?.propertycity);
									// if (res?.suburbcode) addressArray.push(res?.suburbcode);
									if (res?.address) addressArray.push(res?.address);
									if (res?.suburb) addressArray.push(res?.suburb);
									if (res?.city) addressArray.push(res?.city);
									if (res?.postal_code) addressArray.push(res?.postal_code);
									if (res?.claimprovince) addressArray.push(res?.claimprovince);

									return addressArray?.join(', ');
								})
							);
					}
				}
			},
			
			component: 'AddressConfirmationComponent',
			navs: [
				// What Matters nav button disabled – uncomment to re-enable
				// {
				// 	text: 'What Matters',
				// 	location: 'center',
				// 	linkType: 'portal',
				// 	portalData: {
				// 		type: 'actionPanel',
				// 		paramFunc: (instance, mapper) => {
				// 			return {
				// 				id: '1',
				// 				icon: 'what-matters',
				// 				command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
				// 			};
				// 		}
				// 	}
				// },
				{ text: 'Continue', color: 'primary', nextNode: 'JobDetails' }
			]
		},
		JobDetails: {
			name: 'Job Details',
			showTabs: true,
			serverCalls: {
				// claimDetailsInfo: {
				// 	errorMessage: '',
				// 	directCall: (http, store, sq) => {
				// 		return sq
				// 			.queryObject(
				// 				gql`
				// 					{
				// 						allInfo {
				// 							claim_types {
				// 								id
				// 								name
				// 								description
				// 								skill_groups
				// 								priority
				// 							}
				// 							skills {
				// 								id
				// 								name
				// 							}
				// 							config_options {
				// 								desktop {
				// 									upfront_rejection_reasons
				// 								}
				// 								general {
				// 									ia_request_reasons
				// 								}
				// 							}
				// 						}
				// 					}
				// 				`,
				// 				store
				// 			)
				// 			.pipe(
				// 				filter(Boolean),
				// 				take(1)
				// 				// tap((sq) => console.log({ sq })),
				// 				// tap(console.log),
				// 				// map(([claim_types, skills, upfront_rejection_reasons]) => {
				// 				//   const filtClaimTypes = claim_types.map((ite: any) => ite.claim_types.filter((arr) => arr.name !== 'Undefined'));
				// 				//   return {claim_types: filtClaimTypes, skills, upfront_rejection_reasons}
				// 				// }),

				// 				// tap(console.log),
				// 			);
				// 	}
				// },
				voucherTypesData: {
					errorMessage: '',
					directCall: (http, store, sq, bf) => {
						const tenant_id = bf.bigForm.get('selected_company')?.value ?? bf.bigForm.get('tenant_document_config')?.value?.company_id;
						if (!tenant_id) {
							// Guard against race conditions where selected_company is not yet patched.
							return of([]);
						}
						return http.get(`${environment.api_url}v1/voucher_type_action/get_voucher_types/?tenant_id=${tenant_id}`)
						.pipe(
							tap(console.log),
							take(1),
							map(res => {
								if (res.success == true) {
									return res.payload?.voucher_types;
								} else {
									return [];
								}
							})
						);
					}
				},
				storesData: {
					errorMessage: '',
					directCall: (http, store, sq, bf) => {
						const tenant_id = bf.bigForm.get('tenant_document_config')?.value?.company_id;
						if (!tenant_id) {
							return of([]);
						}
						return http.get(`${environment.api_url}v1/poi_action/get_stores?tenant_id=${tenant_id}`).pipe(
							take(1),
							map((res: any) => res?.payload ?? []),
							map((stores: any[]) => {
								return (stores ?? []).map(store => {
									const rawDisplay = store?.name ?? store?.poi_type_name ?? store?.description ?? '';
									// Insert a dash between the tenant prefix and the store name (e.g. "Makro Wonderpark" -> "Makro - Wonderpark")
									// To strip the prefix entirely instead, use: rawDisplay.replace(/^makro\s*/i, '').trim()
									const display = rawDisplay.replace(/^(makro)\s+/i, '$1 - ');
									const value = store?.id ?? store?.mid ?? store?.code ?? display ?? '';
									return { value, display, location: store?.location ?? null };
								});
							})
						);
					}
				}
			},
			initFormFields: (bf) => {
				bf.bigForm.patchValue({ checked_job_details: true });

				// bf.addControl('claim_class', new FormControl(null, Validators.required));
				if (!bf.bigForm.get('selectedClaimClass')) {
					bf.addControl('selectedClaimClass', new UntypedFormControl());
				}
				if (!bf.bigForm.get('claim_class_description')) {
					bf.addControl('claim_class_description', new UntypedFormControl(''));
				}
				
				// Check if jobs array already exists to preserve existing values
				let jobsArray = bf.bigForm.get('jobs') as UntypedFormArray;
				if (!jobsArray) {
					// Create new jobs array with default values
					jobsArray = new UntypedFormArray([bf.fb.group({
						// id: null,
						// skillrequested: [1, [Validators.required]],
						// skillcatagory_id: [null, [Validators.required]],
						// skillcatagory: null,
						// skill_id: [null, [Validators.required]],
						// skill: null,
						// providertype_id: [null, [Validators.required]],
						// providertype: null,
						// appointmentDatePicker: null,
						// appointmentTimePicker: null,
						// appointmentTime: null,
						// range_start: null,
						// range_end: null,
						// amount: [null, [Validators.required, Validators.minLength(2)]],
						// who_collects: [null, [Validators.required]],
						// who_collects_description: null,
						// is_cancelled: false,
						// assessorWaived: false,
						// excess_description: 'Standard Excess',
						// payment_method: [null, [Validators.required]],
						// payment_description: null,
						// delivery_required: false,
						// delivery_amount

						// Voucher selections
						// voucher_id: null,
						// voucher_type: null,
						// voucher_type_id: null,
						voucher_name: null,
						// voucher_description: null,
						// voucher_claim_type: null,
						// voucher_skill: null,
						voucher_terms: null,
						// voucher_product_template: null,

						// Voucher form inputs
						terms: null,
						title: null,
						voucher_id: null,
						skill_id: null,
						total_cost: null,
						delivery_distance: null,
						quantity_available: null,
						appointmentDatePicker: null,
						appointmentTimePicker: null,
						appointmentTime: null,
						range_start: null,
						range_end: null,

					quantity: [1, [Validators.required, Validators.min(1)]],
					store_poi: [null, [Validators.required]],
					what_is_being_installed: null,
					collection_required: false,
					store_has_stock: false,
					delivery_cost: '0.00',
					where_to_collect: null,
				})]);
				bf.addControl('jobs', jobsArray);
				} else {
					// Jobs array exists - ensure all required controls exist in the first job
					// but preserve existing values
					const firstJob = jobsArray.length > 0 ? jobsArray.at(0) as UntypedFormGroup : null;
					if (firstJob) {
						// Ensure all required controls exist, but don't overwrite existing values
						const requiredControls = {
							voucher_terms: null,
							terms: null,
							title: null,
							voucher_id: null,
							voucher_name: null,
							skill_id: null,
							total_cost: null,
							delivery_distance: null,
							quantity_available: null,
							appointmentDatePicker: null,
							appointmentTimePicker: null,
							appointmentTime: null,
							range_start: null,
							range_end: null,
							quantity: [1, [Validators.required, Validators.min(1)]],
							store_poi: [null, [Validators.required]],
							what_is_being_installed: null,
							collection_required: false,
							store_has_stock: false,
							delivery_cost: '0.00',
							where_to_collect: null,
						};

						Object.entries(requiredControls).forEach(([controlName, defaultValue]) => {
							if (!firstJob.get(controlName)) {
								// Control doesn't exist, add it with default value
								if (Array.isArray(defaultValue)) {
									// Handle validators array
									firstJob.addControl(controlName, new UntypedFormControl(defaultValue[0], defaultValue[1]));
								} else {
									firstJob.addControl(controlName, new UntypedFormControl(defaultValue));
								}
							}
							// If control exists, preserve its current value
						});
					} else {
					// Jobs array exists but is empty, add a new job with defaults
					jobsArray.push(bf.fb.group({
						voucher_terms: null,
						terms: null,
						title: null,
						voucher_id: null,
						skill_id: null,
						total_cost: null,
						delivery_distance: null,
						quantity_available: null,
						appointmentDatePicker: null,
						appointmentTimePicker: null,
						appointmentTime: null,
						range_start: null,
						range_end: null,
						quantity: [1, [Validators.required, Validators.min(1)]],
						store_poi: [null, [Validators.required]],
						what_is_being_installed: null,
						collection_required: false,
						store_has_stock: false,
						delivery_cost: '0.00',
						where_to_collect: null,
					}));
					}
				}

				// Dynamically add form controls for additional identities from tenant config
				const additionalIdentities = bf.bigForm.get('tenant_document_config')?.value?.metadata?.additional_identities ?? [];
				const activeJob = (bf.bigForm.get('jobs') as UntypedFormArray)?.at(0) as UntypedFormGroup;
				if (activeJob && additionalIdentities.length > 0) {
					additionalIdentities.forEach((identity: any) => {
						const controlName = `identity_${identity.identity_type}`;
						if (!activeJob.get(controlName)) {
							activeJob.addControl(controlName, new UntypedFormControl(null, [Validators.required]));
						}
					});
				}
			},
			// component: 'VoucherTypesComponent',
			component: {
				children: [
					{
						component: 'VoucherTypesComponent',
						inputs: {
							voucherTypesData$: 'voucherTypesData',
							storesData$: 'storesData'
						}
					},
					{
						component: 'FLXSupportingDocumentFilePreviewComponent',
						inputs: {}
					}
				]
			},
			navs: [
				// What Matters nav button disabled – uncomment to re-enable
				// {
				// 	text: 'What Matters',
				// 	location: 'center',
				// 	linkType: 'portal',
				// 	portalData: {
				// 		type: 'actionPanel',
				// 		paramFunc: (instance, mapper) => {
				// 			return {
				// 				id: '1',
				// 				icon: 'what-matters',
				// 				command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
				// 			};
				// 		}
				// 	}
				// },
				{ text: 'Continue', color: 'primary', nextNode: 'ClaimAppointments' }
			]
		},
		ClaimAppointments: {
			name: 'Appointments',
			initFormFields: bf => {
				bf.bigForm.patchValue({ checked_appointments: true });

				bf.addControl(
					'on_site_person',
					new UntypedFormControl('', [
						Validators.required,
						Validators.minLength(2),
						CustomValidators.hardMaxLength(140),
						CustomValidators.onlyAllowedASCII([32, 39, 44, 46, ...generateRange(65, 90, 1), ...generateRange(96, 122, 1)])
					])
				);
				bf.addControl('on_site_notes', new UntypedFormControl('', [CustomValidators.onlyAllowedASCII([10, 13]?.concat(generateRange(32, 126, 1)))]));
				bf.addControl(
					'on_site_contact',
					new UntypedFormControl('', [
						Validators.required,
						CustomValidators.numeric,
						Validators.minLength(10),
						CustomValidators.onlyAllowedASCII(generateRange(48, 57, 1)),
						CustomValidators.hardMaxLength(12)
					])
				);
				bf.addControl('timepicker', new UntypedFormControl(''));
				bf.addControl('selectedDateType', new UntypedFormControl(''));
				bf.addControl('isSameAppointment', new UntypedFormControl(''));
				bf.addControl('appointments', new UntypedFormArray([]));
			},
			showTabs: true,
			inputs: {
				minHour: APPOINTMENT_MIN_HOUR,
				maxHour: APPOINTMENT_MAX_HOUR,
				minDate: getAppointmentEarliestDateTime(),
				allowSundays: false,
				checkAfterHours: false
			},
			serverCalls: {
				appointmentTypes: {
					errorMessage: '',
					directCall: (http, store, sq) => {
						return sq
							.queryObject(
								gql`
									{
										allInfo {
											appointment_types {
												id
												name
											}
										}
									}
								`,
								store
							)
							.pipe(
								filter(Boolean),
								take(1),
								map(values),
								map<any, any>(flatten)
							);
					}
				},
				appointmentData: {
					errorMessage: '',
					directCall: (http, store, sq) => {
						return sq
							.queryObject(
								gql`
									{
										allInfo {
											appointment_types {
												id
												name
											}
										}
									}
								`,
								store
							)
							.pipe(
								filter(Boolean),
								take(1),
								map(values),
								map<any, any>(flatten)
							);
					}
				}
			},
			component: 'ClaimAppointmentsComponent',
			navs: [
				// What Matters nav button disabled – uncomment to re-enable
				// {
				// 	text: 'What Matters',
				// 	location: 'center',
				// 	linkType: 'portal',
				// 	portalData: {
				// 		type: 'actionPanel',
				// 		paramFunc: (instance, mapper) => {
				// 			return {
				// 				id: '1',
				// 				icon: 'what-matters',
				// 				command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
				// 			};
				// 		}
				// 	}
				// },
				{
					text: 'Continue',
					color: 'primary',
					nextNode: 'TermsAndConditions',
					beforeNextNode: (navComponent: any, _onSuccess: () => void) => {
						void _onSuccess;
						const earliestDateTime = getAppointmentEarliestDateTime();
						const jobs = navComponent?.bf?.bigForm?.get('jobs')?.value ?? [];
						const upfrontrepudiation = !!navComponent?.bf?.bigForm?.get('upfrontrepudiation')?.value;
						const hasInvalidAppointments = hasInvalidAppointmentSelection(jobs, upfrontrepudiation, earliestDateTime);
						if (hasInvalidAppointments) {
							const modalService = navComponent?.newModalService ?? navComponent?.modalService;
							modalService?.openModalDirectly((inst: any) => {
								inst.type = 'warning';
								inst.heading = 'Invalid appointment selection';
								inst.setMessage([
									'Appointments must be at least 2 days from now (keeping the current hour as the reference).',
									'Allowed time is 08:00 to 17:00, and Sundays are not allowed.'
								]);
								inst.navButtons = [{ text: 'OK', color: 'primary', linkType: 'close' }];
								inst.closeButton = false;
							});
							return;
						}
						const analyseFilesWithAi =
							navComponent?.bf?.bigForm?.get('tenant_document_config')?.value?.analyse_files_with_ai === true;
						if (analyseFilesWithAi) {
							navComponent.nextComponentKey('TermsAndConditions');
						} else {
							navComponent.nextComponentKey('SupportingDocumentUpload');
						}
					}
				}
			]
		},
		TermsAndConditions: {
			name: 'T&C',
			showTabs: true,
			initFormFields: (bf) => {
				const fallbackText = 'Please select a voucher to view the associated terms and conditions.';
				// Terms acceptance checkbox
				const acceptControlName = 'accept_terms_and_conditions';
				let acceptControl = bf.bigForm.get(acceptControlName);
				if (!acceptControl) {
					acceptControl = new UntypedFormControl(false, [Validators.requiredTrue]);
					bf.bigForm.addControl(acceptControlName, acceptControl);
				} else {
					acceptControl.setValidators([Validators.requiredTrue]);
					if (acceptControl.value !== true) {
						acceptControl.setValue(false, { emitEvent: false });
					}
					acceptControl.updateValueAndValidity({ emitEvent: false });
				}

				let termsControl = bf.bigForm.get('terms_and_conditions');
				if (!termsControl) {
					termsControl = new UntypedFormControl(fallbackText);
					bf.bigForm.addControl('terms_and_conditions', termsControl);
				} else if (!termsControl.value) {
					termsControl.setValue(fallbackText);
				}
				
				const buildTermsText = (voucherTerms: any): string => {
					if (!voucherTerms) {
						return fallbackText;
					}
					const parts: string[] = [];
					if (typeof voucherTerms.inclusions === 'string' && voucherTerms.inclusions.trim()) {
						parts.push('Included\r\n' + voucherTerms.inclusions.trim());
					}
					if (typeof voucherTerms.exclusions === 'string' && voucherTerms.exclusions.trim()) {
						parts.push('Excluded\r\n' + voucherTerms.exclusions.trim());
					}
					if (typeof voucherTerms.full_text === 'string' && voucherTerms.full_text.trim()) {
						parts.push('Full Terms and Conditions\r\n' + voucherTerms.full_text.trim());
					}
					return parts.length > 0 ? parts.join('\r\n\r\n') : fallbackText;
				};

				const jobs = bf.bigForm.get('jobs') as UntypedFormArray;
				const firstJob = jobs?.length ? jobs.at(0) : null;
				const voucherTermsControl = firstJob?.get('voucher_terms');
				const updateTerms = () => {
					const voucherTerms = voucherTermsControl?.value;
					termsControl.setValue(buildTermsText(voucherTerms));
				};

				updateTerms();

				if (voucherTermsControl) {
					return voucherTermsControl.valueChanges.pipe(
						tap(() => updateTerms())
					).subscribe();
				}

				return null;
			},
			// component: 'FLXFlatDynamicFormComponent',
			component: {
				children: [
					{
						component: 'FLXHeadingWithInstructionsComponent',
						inputs: {
							title: 'Please ask the customer to read and accept these terms',
							instructions: ['A copy of this will be emailed to the  customer']
						}
					},
					{
						component: 'FLXFlatDynamicFormComponent',
						inputs: {
				
							formControls: {
								0: {
									label: '',
									inputType: 'textarea',
									formControlName: 'terms_and_conditions',
									rows: 10,
									placeholder: 'Please select a voucher to view the associated terms and conditions.',
									// maxWidth: '678px'
								},
								1: {
									label: 'As the customer I accept the terms and conditions',
									inputType: 'checkbox',
									formControlName: 'accept_terms_and_conditions',
									defaultValue: false,
									margin: '-30px 0 10px 0',
									maxWidth: '567px'
								}
							},
							formLayout: 'stacked',
							width: '100%',
							containerWidth: '100%',
							headingSize: 'medium',
							headingWeight: 'normal',
							headingType: 'creation',
						
						},
					}
				]
			},
			navs: [
				{
					text: 'Continue',
					color: 'primary',
					nextNode: 'ClaimSubmissionSummary'
				}
			]
		},
		
		ClaimSubmissionSummary: {
			name: 'Submit',
			showTabs: true,
			initFormFields: (bf) => {
				const clientDetails = bf.bigForm.get('client_details') ? bf.bigForm.get('client_details')?.value?.policy_number : '';
				if (bf.bigForm.get('underwriter')) {
					bf.bigForm.get('underwriter').patchValue(clientDetails);
				} else {
					bf.addControl('underwriter', new UntypedFormControl(clientDetails));
				}

				syncClaimSubmissionValidation(bf);
				return bf.bigForm.valueChanges
					.pipe(
						startWith(bf.bigForm.value),
						tap(() => syncClaimSubmissionValidation(bf))
					)
					.subscribe();
			},
			// component: 'FLXFlatDynamicFormComponent',
			component: {
				children: [
					{
						component: 'FLXFlatDynamicFormComponent',
						inputs: {
							heading: 'Submit Claim To Proceed',
							formControls: {
								// 0: { label: "Don't notify Sps. This claim is for data capture only.", inputType: 'checkbox', formControlName: 'dontpingsp' },
								// 1: {
								// 	label: 'Upfront repudiation. Repudiate this claim now.',
								// 	inputType: 'checkbox',
								// 	formControlName: 'upfrontrepudiation',
								// 	defaultValue: false
								// }
							},
							formLayout: 'stacked',
							width: '200px',
							containerWidth: '340px',
							headingSize: 'medium',
							headingWeight: 'extra-light',
							headingType: 'creation'
						}
					},
					{
						component: 'FLXAlertMessagesComponent',
						inputs: { alertMessages$: 'alertMessages' }
					}
				]
			},
			serverCalls: {
				alertMessages: {
					errorMessage: 'Could not retrieve the form errors',
					directCall: (http, store, sq, bf) => {
						if (bf.bigForm.get('jobs')) {
							const jobs = bf.bigForm?.value?.jobs;

							jobs.forEach((job, index) => {
								if (job.skill === 'Loss Adjusting' || job?.skill === 'Internal Assessor') {
									if (bf.bigForm.get(`jobs.${index}`).get('assessorWaived')) {
										bf.bigForm.get(`jobs.${index}`).get('assessorWaived')?.setValue(true);
									}
								} else {
									if (bf.bigForm.get(`jobs.${index}`).get('assessorWaived')) {
										bf.bigForm.get(`jobs.${index}`).get('assessorWaived')?.setValue(false);
									}
								}
							});
						}

						if (bf.bigForm.get('upfrontrepudiation') && !!bf.bigForm.get('upfrontrepudiation')?.value) {
							toggleUpfrontValidation(!!bf.bigForm.get('upfrontrepudiation')?.value, bf.bigForm);
						} else {
							checkBusinessRules(bf.bigForm);
						}
						bf.bigForm?.updateValueAndValidity();
						const formChanges$ = merge(
							bf.bigForm?.valueChanges ?? empty(),
							bf.bigForm?.statusChanges ?? empty()
						).pipe(startWith(undefined));
						return formChanges$.pipe(
							debounceTime(400),
							tap(() => syncClaimSubmissionValidation(bf)),
							switchMap(() => bf.retrieveErrors$()),
							map((errors: string[]) => {
								// Show success when there are no validation errors (all required fields captured)
								if (errors.length === 0) {
									return { type: 'success', messages: ['All information captured. You are good to submit!'] };
								}
								return { type: 'error', messages: errors };
							})
						);
					}
				}
			},
			navs: [
				// What Matters nav button disabled – uncomment to re-enable
				// {
				// 	text: 'What Matters',
				// 	location: 'center',
				// 	linkType: 'portal',
				// 	portalData: {
				// 		type: 'actionPanel',
				// 		paramFunc: (instance, mapper) => {
				// 			return {
				// 				id: '1',
				// 				icon: 'what-matters',
				// 				command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
				// 			};
				// 		}
				// 	}
				// },
				{
					text: 'Save as Draft',
					nextNode: 'DraftSubmissionSuccess',
					serverFirst: true,
					serverCalls: {
						saveAsServerDraft: {
							errorMessage: 'Could not save the draft!',
							serviceVariable: 'pggService',
							functionName: 'checkNetworkAndSaveDraft',
							followUpSuccessCalls: {
								deleteCurrentEditableClaim: {
									errorMessage: 'Could not delete the current editable claim!',
									serviceVariable: 'buiService',
									functionName: 'deleteCurrentEditableItem'
								}
							}
						}
					}
				},
				// {
				// 	text: 'Submit, update, upload',
				// 	linkType: 'submit',
				// 	color: 'primary',
				// 	nextNode: 'SubmissionSuccess',
				// 	serverFirst: true,
				// 	serverCalls: {
				// 		response: {
				// 			errorMessage: 'This claim could not be created!',
				// 			serviceVariable: 'pggService',
				// 			functionName: 'createClaim',
				// 			followUpSuccessCalls: {
				// 				uploadSupportingDocuments: {
				// 					errorMessage: 'Documents could not be uploaded',
				// 					serviceVariable: 'pggService',
				// 					functionName: 'uploadSupportingDocumentsWithClaimId'
				// 				},
				// 				deleteCurrentEditableClaim: {
				// 					errorMessage: 'Could not delete the current editable claim!',
				// 					serviceVariable: 'pggService',
				// 					functionName: 'deleteCurrentEditableItem'
				// 				},
				// 				clearCachedClaims: {
				// 					errorMessage: 'Could not clear cached claims!',
				// 					directCall: (http, store, sq, bf, controller, modal) => {
				// 						if (!!bf.bigForm?.value?.is_cat === true) {
				// 							modal.openModalDirectly(inst => {
				// 								inst.heading = 'Appointment will be ignored';
				// 								inst.type = 'info';
				// 								inst.subheading = 'This claim is part of a catastrophe.';
				// 								inst.message = 'Notify customer that appointment times cannot be met based on the volume of claims.';
				// 								inst.navButtons = [
				// 									{
				// 										text: 'Continue',
				// 										color: 'primary',
				// 										linkType: 'close'
				// 									}
				// 								];
				// 								inst.closeButton = false;
				// 							});
				// 						}
				// 						bf.bigForm.reset();
				// 						Object.keys(bf.bigForm.controls)?.forEach(control => bf.bigForm.removeControl(control));
				// 						controller.dispatch(
				// 							new ChangeManifestState(state => {
				// 								return {
				// 									...state,
				// 									viewData: {
				// 										...state.viewData,
				// 										default: []
				// 									}
				// 								};
				// 							})
				// 						);
				// 						return empty();
				// 					}
				// 				}
				// 			},
				// 			followUpFailCalls: {
				// 				createLocalDraft: {
				// 					errorMessage: 'Could not create the claim in local draft',
				// 					serviceVariable: 'pggService',
				// 					functionName: 'saveAsLocalDraft',
				// 					followUpSuccessCalls: {
				// 						deleteCurrentEditableClaim: {
				// 							errorMessage: 'Could not delete the current editable claim!',
				// 							serviceVariable: 'pggService',
				// 							functionName: 'deleteCurrentEditableItem'
				// 						},
				// 						navigateToWorkflow: {
				// 							errorMessage: '',
				// 							serviceVariable: 'pggService',
				// 							functionName: 'navigateToWorkflow'
				// 						}
				// 					}
				// 				}
				// 			}
				// 		}
				// 	}
				// },
				{
					text: 'Submit',
					linkType: 'submit',
					optIntoValidation: true,
					color: 'primary',
					nextNode: 'SubmissionSuccess',
					serverFirst: true,
					serverCalls: {
						response: {
							errorMessage: 'This claim could not be created!',
							serviceVariable: 'pggService',
							functionName: 'submitClaimWithOfflineFallback',
							followUpSuccessCalls: {
								deleteCurrentEditableClaim: {
									errorMessage: 'Could not delete the current editable claim!',
									serviceVariable: 'pggService',
									functionName: 'deleteCurrentEditableItem'
								},
								clearCachedClaims: {
									errorMessage: 'Could not clear cached claims!',
									directCall: (http, store, sq, bf, controller, modal) => {
										if (!!bf.bigForm?.value?.is_cat === true) {
											modal.openModalDirectly(inst => {
												inst.heading = 'Appointment will be ignored';
												inst.type = 'info';
												inst.subheading = 'This claim is part of a catastrophe.';
												inst.message = 'Notify customer that appointment times cannot be met based on the volume of claims.';
												inst.navButtons = [
													{
														text: 'Continue',
														color: 'primary',
														linkType: 'close'
													}
												];
												inst.closeButton = false;
											});
										}
										bf.bigForm.reset();
										Object.keys(bf.bigForm.controls)?.forEach(control => bf.bigForm.removeControl(control));
										controller.dispatch(
											new ChangeManifestState(state => {
												return {
													...state,
													viewData: {
														...state.viewData,
														default: []
													}
												};
											})
										);
										return empty();
									}
								}
							},
							followUpFailCalls: {
								createLocalDraft: {
									errorMessage: 'Could not create the claim in local draft',
									serviceVariable: 'pggService',
									functionName: 'saveAsLocalDraft',
									followUpSuccessCalls: {
										deleteCurrentEditableClaim: {
											errorMessage: 'Could not delete the current editable claim!',
											serviceVariable: 'pggService',
											functionName: 'deleteCurrentEditableItem'
										},
										navigateToWorkflow: {
											errorMessage: '',
											serviceVariable: 'pggService',
											functionName: 'navigateToWorkflow'
										}
									}
								}
							}
						}
					}
				}

			]
		},
		SubmissionSuccess: {
			hideTabItem: true,
			component: 'FLXSuccessTickComponent',
			serverCalls: {
				copyText: {
					errorMessage: 'Could not retrieve new claim number!',
					directCall: (http, store, sq) => {
						return sq
							.queryObject(
								gql`
									{
										selectedContext {
											response {
												payload {
													mid
												}
											}
										}
									}
								`,
								store
							)
							.pipe(
								filter(Boolean),
								take(1),
								map(values),
								map<any, any>(flatten),
								switchMap((val: any) => {
									if (val) return val;
									else return null;
								}),
								tap(() => {
									store.dispatch(new CollapseActionPanel());
								})
							);
					}
				}
			},
			inputs: {
				copyTextHeading: 'Claim Number',
				autoClose: false,
				showContinueButton: true,
				continueText: 'Continue'
			},
			navs: [
				{
					text: 'New Voucher',
					color: 'secondary',
					disableOnFirstClick: true,
					nextNode: 'CompanySelection',
					serverFirst: true,
					serverCalls: {
						clearSupportingDocuments: {
							errorMessage: '',
							serviceVariable: 'pggService',
							functionName: 'clearAllSupportingDocuments'
						}
					},
					visible: (_bf, store) => {
						return store.select(getCurrentUser).pipe(
							filter(Boolean),
							take(1),
							map((user: any) => {
								const branches = user?.user?.branches ?? [];
								return Array.isArray(branches) && branches.length > 1;
							})
						);
					}
				}
			]
		},
		DraftSubmissionSuccess: {
			hideTabItem: true,
			component: 'FLXSuccessTickComponent',
			inputs: {
				heading: 'Draft saved successfully. You can return to the workflow.',
				autoClose: false,
				showContinueButton: true,
				continueText: 'Continue'
			}
		},
		
	},
	// bigFormToStoreMapper is now a function that merges static mappings with dynamic tenant-specific mappings
	bigFormToStoreMapper: ((bf: any) => buildCompleteMapper(bf)) as any,
	flowErrorMessages: {
		'checked_supporting_document_upload:required': 'Please select the supporting documents',
		'checked_supporting_document_upload:requiredLocalDocuments':
			'Please upload and store all required supporting documents before submitting.',
		'checked_customer_details:required': 'Please fill in the Customer Details',
		'checked_address_confirmation:required': 'Please confirm the address on the Map.',
		'checked_job_details:required': 'Please fill in the Job Details.',
		'checked_appointments:required': 'Please set a valid appointment (08:00-17:00, at least 2 days from now, excluding Sundays).',
		'accept_terms_and_conditions:required': 'Please accept the terms and conditions.',
		'accept_terms_and_conditions:requiredTrue': 'Please accept the terms and conditions.',
		// 'customer_reference:required': 'Customer reference is required (in Customer Details)',
		// 'sales_order_number:required': 'Sales order number is required (in Customer Details)',
		// 'who_collects:required': 'Specify who collects excess',
		// 'payment_method:required': 'Specify payment method',
		// 'clientcode:required': 'Please search for a Valid Policy (Policy Lookup)',
		// 'claim_class:required': 'Claim class is required (Claim Details)',
		'jobs:required': 'At least one job card request is needed (Job Details)',
		'store_poi:required': 'Please select Store Purchase From (in Job Details).',
		'appointmentDatePicker:flxDatepickerMin': 'Job appointment date must be at least 2 days from now (excluding Sundays).',
		// 'complex_unit_number:required': 'Complex unit number is required (in Customer Details)',
		'street_address:required': 'Property street address is required (in Customer Details)',
		'suburb:required': 'Property suburb is required (in Customer Details)',
		'city:required': 'Property city is required (in Customer Details)',
		'postal_code:required': 'Area code is required (in Customer Details)',
		
		'email:required': 'Email address is required (in Customer Details)',
		'email:email': 'You need to enter a valid email address (in Customer Details)',
		'cell_number:cellphone': 'Cell number is invalid (in Customer Details)',
		'cell_number:required': 'Cell number is required (in Customer Details)',
		'contact_number:contact_number': 'Contact number is invalid (in Customer Details)',
		'contact_number:required': 'Contact number is required (in Customer Details)',
		'preferred_com_method:required': 'Communication method is required (in Customer Details)',
		'addressconfirmation:required': 'Please confirm property address',
		'longitude:required': 'No address longitude has been picked up (in Maps)',
		'latitude:required': 'No address latitude has been picked up (in Maps)',
		'province:required': 'Province is required (in Customer Details)',
		'first_name:required': 'Applicant first name is required (in Customer Details)',
		'first_name:minlength': 'First name needs a minimum of 2 characters (in Customer Details)',
		'first_name:onlyAllowedASCII': 'First name field contains invalid character(s) (in Customer Details)',
		'surname:required': 'Applicant surname is required (in Customer Details)',
		'first_name:text': 'Applicant first name has additional characters (in Customer Details)',
		'id_number:required': 'Applicant ID number is required (in Customer Details)',
		// 'historyfetchedflag:required': 'No history lookup has been performed',
		'appointmentDatePicker:required': 'Job appointment date is required (in Appointments)',
		'appointmentTimePicker:required': 'Job appointment time is required (in Appointments)',
		'on_site_contact:required': 'On site contact number required (in Appointments)',
		'on_site_contact:contact_number': 'On site contact number is invalid (in Appointments)',
		'on_site_person:required': 'On site person name is required (in Appointments)'
	}
};




// PolicyLookup: {
// 	name: 'Policy Lookup',
// 	showTabs: true,
// 	errorHandler: {
// 		retryPolicy: 'manual',
// 		displayFormat: 'none',
// 		onRetryComplete: () => {
// 			return empty();
// 		}
// 	},
// 	initFormFields: (bf, item, inst, sq, store) => {
// 		store
// 			.select(getCurrentUser)
// 			.pipe(take(1))
// 			.subscribe(user => {
				
// 				let { full_name, contact_number } = user?.user;
// 				let email_address = user?.email;
// 				let isBuiAgent = true;
// 				if (user?.user?.roles?.includes(18)) {
// 					console.log('After hours agent');
// 					full_name = null;
// 					contact_number = null;
// 					email_address = null;
// 					isBuiAgent = false;
// 				}
// 				bf.bigForm.addControl('bui_agent', new UntypedFormControl(isBuiAgent));
// 				bf.bigForm.addControl('handler', new UntypedFormControl(full_name));
// 				bf.bigForm.addControl('handlercontact', new UntypedFormControl(contact_number));
// 				bf.bigForm.addControl('handleremail', new UntypedFormControl(email_address));
// 			});
// 		// To check that the screens have been visited
// 		bf.bigForm.addControl('checked_policy_details', new UntypedFormControl(null));
// 		bf.bigForm.addControl('checked_address_confirmation', new UntypedFormControl(null));
// 		bf.bigForm.addControl('checked_claim_details', new UntypedFormControl(null));
// 		bf.bigForm.addControl('checked_appointments', new UntypedFormControl(null));
// 		bf.bigForm.addControl('checked_excess', new UntypedFormControl(null));

// 		// bf.patchValues({ policy_number: '' });
// 		bf.bigForm.addControl('new_state', new UntypedFormControl(1));
// 		bf.bigForm.addControl('sub_section', new UntypedFormControl(1));
// 		bf.bigForm.addControl('clientcode', new UntypedFormControl(null, [Validators.required]));
// 		bf.bigForm.addControl('claim_class', new UntypedFormControl(null, [Validators.required]));
// 		bf.bigForm.addControl('policy_lookup', new UntypedFormControl('', [CustomValidators.alphaNumericWithForwardSlash, Validators.minLength(2), Validators.maxLength(15)]));
// 		bf.bigForm.addControl('dontpingsp', new UntypedFormControl(''));
// 		bf.bigForm.addControl('upfrontrepudiation', new UntypedFormControl(false));

// 		bf.addControl('claim_class', new UntypedFormControl(null, Validators.required));

// 		// For maps initialisation. Otherwise validators do not pick it up
// 		let suburb_code, latitude, longitude, province, area_code;

// 		if (bf.bigForm.get('latitude')) {
// 			latitude = bf.bigForm.get('latitude')?.value;
// 			bf.bigForm.get('latitude')?.setValidators([Validators.required]);
// 		} else {
// 			bf.addControl('latitude', new UntypedFormControl(null, [Validators.required]));
// 		}

// 		if (bf.bigForm.get('longitude')) {
// 			longitude = bf.bigForm.get('longitude')?.value;
// 			bf.bigForm.get('longitude')?.setValidators([Validators.required]);
// 		} else {
// 			bf.addControl('longitude', new UntypedFormControl(null, [Validators.required]));
// 		}

// 		if (bf.bigForm.get('suburb_code')) {
// 			suburb_code = bf.bigForm.get('suburb_code')?.value;
// 			// bf.bigForm.get('suburb_code')?.setValidators([Validators.required]);
// 		} else {
// 			bf.addControl('suburb_code', new UntypedFormControl(null));
// 		}

// 		if (bf.bigForm.get('province')) {
// 			province = bf.bigForm.get('province')?.value;
// 			bf.bigForm.get('province')?.setValidators([Validators.required]);
// 		} else {
// 			bf.addControl('province', new UntypedFormControl(null, [Validators.required]));
// 		}

// 		if (bf.bigForm.get('area_code')) {
// 			area_code = bf.bigForm.get('area_code')?.value;
// 			bf.bigForm.get('area_code')?.setValidators([Validators.required]);
// 		} else {
// 			bf.addControl('area_code', new UntypedFormControl(null, [Validators.required]));
// 		}

// 		if (!latitude || !longitude) {
// 			latitude = null;
// 			longitude = null;
// 		}
// 	},
// 	checkValidityForFields: [],
// 	inputs: {
// 		title: 'Search a Policy Number, Bond Account Number or ID Number',
// 		validPolicyStates: ['HLC', 'BMB', 'HLB', 'EBK', 'STH', 'SHS'],
// 		buicodes: ['HLC', 'BMB', 'HLB', 'EBK'],
// 		stansurecodes: ['STH', 'SHS'],
// 		lookupEndpoint: {
// 			dataKey: 'policies',
// 			serviceVariable: 'buiService',
// 			functionName: 'policyLookup',
// 			ignoreFalseError: true,
// 			// responseSlice: 'payload.policies',
// 			errorMessage: ' No policy found.',
// 			timeoutMilliseconds: 120000 // 2  minutes
// 		},
// 		historyLookupEndpoint: {
// 			dataKey: 'claimHistory',
// 			serviceVariable: 'buiService',
// 			isBackgroundTask: true,
// 			functionName: 'policyHistorySearch',
// 			responseSlice: 'payload.claims',
// 			displayError: false
// 		}
// 	},
// 	hasLoader: false,
// 	component: 'PolicyLookupComponent',
// 	navs: [
// 		{
// 			text: 'What Matters',
// 			location: 'center',
// 			linkType: 'portal',
// 			portalData: {
// 				type: 'actionPanel',
// 				paramFunc: (instance, mapper) => {
// 					return {
// 						id: '1',
// 						icon: 'what-matters',
// 						command: instance.loadComponent.bind(instance, mapper['FLXWhatMattersComponent'])
// 					};
// 				}
// 			}
// 		},
// 		{ text: 'Continue', color: 'primary', nextNode: 'CustomerDetails' }
// 	]
// },